/**
 * DEMO STUB — replaced by PROD-1316 intent service.
 *
 * Deterministic regex/keyword parser for the Scheduling-AI command bar.
 * NO model call, NO network — every utterance parses the same way every
 * time, which is exactly what a founder demo needs. The production
 * replacement (PROD-1316) swaps this module for the intent-service
 * client behind the same `SchedulingCommandDraft` contract; nothing
 * downstream (prefill builder, ghost rows, alternatives wiring) changes.
 *
 * Utterance shapes covered (case-insensitive):
 *
 *   A) "book <name> <profile-ish words> [with <doctor>] [monthly|weekly]
 *       [for N months|weeks] [on] <weekday|tomorrow|today> <time>"
 *      e.g. "book Sarah Khan follow up with Rose Logan monthly for 6 months on Tuesday 10am"
 *
 *   B) "book <name> <weekday|tomorrow|today> <time> [duration]"
 *      e.g. "book John Smith tomorrow 2:30pm 45 min"
 *
 *   C) back-to-back chain — segments joined by "then"; the shared
 *      day/time places the FIRST visit, every later segment starts when
 *      the previous one ends (receptionist-on-the-phone flow):
 *      "book Sarah Khan follow up with Rose Logan then physio with
 *       Jack Reacher tomorrow 10am 30 min"
 *
 * Splitting heuristic (documented demo behaviour, not NLP): after "book",
 * tokens are consumed until the first STOP token (weekday / tomorrow /
 * today / time / recurrence keyword / "for" / "on" / "every" / "with" /
 * "then"). The first two tokens of that span are the patient query
 * (first + last name); anything left over is the profile query
 * ("follow up", "physio", …). "with <doctor…>" collects name tokens
 * (a leading "dr"/"dr." is dropped) until the next structural token.
 */

const WEEKDAY_NAMES = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

/** 3-letter abbreviation → canonical weekday (display-cased). */
const WEEKDAY_BY_TOKEN = new Map<string, string>(
  WEEKDAY_NAMES.flatMap((d) => {
    const display = d.charAt(0).toUpperCase() + d.slice(1);
    return [
      [d, display],
      [d.slice(0, 3), display],
    ] as [string, string][];
  }),
);

/** "10am" / "2:30pm" / "14:00" — hour, optional minutes, optional meridiem. */
const TIME_RE = /^(\d{1,2})(?::(\d{2}))?(am|pm)?$/;

export interface SchedulingCommandRecurrence {
  /** Backend recurrence period enum subset the parser can produce. */
  period: 'Weeks' | 'Months';
  /** Number of occurrences to draft (numberOfRepeat). */
  count: number;
  /** Canonical weekday display name, e.g. "Tuesday". */
  weekday: string;
  /** 24h "HH:mm". */
  time: string;
}

/** One later visit in a back-to-back chain (shape C). */
export interface SchedulingCommandChainSegmentResolved {
  /** Roster practitioner id when the LIVE intent lane matched confidently
   *  (the stub grammar never sets ids — they come from PROD-1316 only). */
  practitionerId?: string;
  /** Visit-profile id when the live lane matched confidently. */
  profileId?: string;
}

export interface SchedulingCommandChainSegment extends SchedulingCommandChainSegmentResolved {
  /** Free-text appointment-profile hint for this visit. */
  profileQuery?: string;
  /** Free-text practitioner hint ("rose logan" — "dr" stripped). */
  doctorQuery?: string;
  /** Per-visit duration when the segment carried one. */
  durationMinutes?: number;
}

export interface SchedulingCommandDraft {
  /** The utterance as typed/dictated — the completion rail's RequestHeader
   *  quotes it verbatim so the user sees exactly what was understood. */
  raw?: string;
  /** WHO read the sentence: 'model' (live intent lane) or 'grammar' (the
   *  offline stub). Andrew on #843: a 0.99 model parse and a regex guess
   *  must not look identical — the rail marks grammar drafts so a partial
   *  offline parse can't masquerade as understanding. */
  source?: 'model' | 'grammar';
  /** Free-text patient search seed — fed to the panel's patient search. */
  patientQuery: string;
  /** Patient id ONLY when a human already confirmed this patient in THIS
   *  request — today that is solely a chain advance carrying the patient
   *  the user picked for the prior leg (audit #4). Neither parser lane
   *  ever sets it: patient identity resolution stays a human act
   *  (assurance-model rule, Person module SB/1729560577 — "someone should
   *  select the appropriate record"). */
  patientId?: string;
  /** Plain-words questions the MODEL flagged about its own parse (live
   *  lane only, incl. the <0.85 confidence-floor doubt). The rail renders
   *  these as asks — surfacing what the model already knows it's unsure
   *  about instead of applying a doubted guess (audit #5). */
  asks?: string[];
  /** Free-text appointment-profile hint ("follow up", "physio"). */
  profileQuery?: string;
  /** Free-text practitioner hint for the FIRST visit ("rose logan"). */
  doctorQuery?: string;
  /** Roster practitioner id when the LIVE intent lane (PROD-1316) matched
   *  confidently — the frame prefers it over fuzzy name matching. The stub
   *  grammar never sets it. */
  practitionerId?: string;
  /** Visit-profile id when the live lane matched confidently. */
  profileId?: string;
  recurrence?: SchedulingCommandRecurrence;
  /** Local no-Z ISO (`YYYY-MM-DDTHH:mm:ss`) of the first parsed occurrence. */
  start?: string;
  durationMinutes?: number;
  /** Back-to-back visits AFTER the first — each starts at the prior end. */
  chain?: SchedulingCommandChainSegment[];
  /**
   * Labels of chain visits ALREADY BOOKED in this request (advanceAiChain
   * pops booked legs out of `chain` and appends their label here, so the
   * completion rail keeps showing the full journey with done rows instead
   * of the progress resetting every save — Codex P2 on the rail commit).
   */
  chainDone?: string[];
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Local, no-`Z` ISO — same wire form the scheduling module already uses
 *  (`quick-create-slot.ts` `toLocalNoZISO`; the .NET binder parses a
 *  trailing `Z` wrong). */
function toLocalNoZISO(d: Date): string {
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

/** Parse a time token to 24h {hour, minute}; null when not a time. */
function parseTimeToken(token: string): { hour: number; minute: number } | null {
  const m = TIME_RE.exec(token);
  if (!m) return null;
  let hour = Number(m[1]);
  const minute = m[2] ? Number(m[2]) : 0;
  const meridiem = m[3];
  if (Number.isNaN(hour) || Number.isNaN(minute) || minute > 59) return null;
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    if (meridiem === 'pm' && hour !== 12) hour += 12;
    if (meridiem === 'am' && hour === 12) hour = 0;
  } else {
    // Bare number without meridiem: accept 24h form only when it carries
    // minutes ("14:00") — a bare "10" is ambiguous and rejected so the
    // duration token ("45 min") can't be eaten as a time.
    if (!m[2]) return null;
    if (hour > 23) return null;
  }
  return { hour, minute };
}

/**
 * Next occurrence of `weekday` at `time`, from `now`. Same-day resolves
 * to today when the time is still ahead; otherwise next week's day.
 */
function nextOccurrence(
  weekdayDisplay: string,
  hour: number,
  minute: number,
  now: Date,
): Date {
  const target = WEEKDAY_NAMES.indexOf(
    weekdayDisplay.toLowerCase() as (typeof WEEKDAY_NAMES)[number],
  );
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute, 0, 0);
  let delta = (target - d.getDay() + 7) % 7;
  if (delta === 0 && d.getTime() <= now.getTime()) delta = 7;
  d.setDate(d.getDate() + delta);
  return d;
}

/** "today"/"tomorrow" at `time`, relative to `now`. */
function relativeDay(offsetDays: number, hour: number, minute: number, now: Date): Date {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute, 0, 0);
  d.setDate(d.getDate() + offsetDays);
  return d;
}

interface ParsedSegment {
  span: string[];
  doctorWords: string[];
  durationMinutes?: number;
}

/**
 * Natural-dictation tokenizer: receptionists type (or dictate) with commas,
 * sentence periods and separated meridiems — "Book Sara Khan to follow up
 * with Rose Logan, then physio with Jack Reacher tomorrow at 10 a.m" must
 * parse (live failure 2026-08-11). Rules:
 *   - commas/semicolons/! /? are token separators;
 *   - trailing periods are stripped ("a.m." → "a.m", end-of-sentence);
 *   - "a.m"/"p.m" normalize to am/pm;
 *   - a bare hour followed by am/pm merges into one time token ("10 a.m"
 *     → "10am"), and "10 : 30" survives as "10:30".
 */
function tokenize(utterance: string): string[] {
  const raw = utterance
    .toLowerCase()
    .replace(/[,;!?]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => t.replace(/\.+$/g, ''))
    .map((t) => (t === 'a.m' ? 'am' : t === 'p.m' ? 'pm' : t))
    .filter(Boolean);

  const merged: string[] = [];
  for (let i = 0; i < raw.length; i++) {
    const t = raw[i];
    const next = raw[i + 1];
    if ((next === 'am' || next === 'pm') && /^\d{1,2}(?::\d{2})?$/.test(t)) {
      merged.push(`${t}${next}`);
      i++;
      continue;
    }
    merged.push(t);
  }
  return merged;
}

/** Filler words skipped everywhere — they carry no scheduling meaning. */
const FILLERS = new Set(['on', 'at', 'to', 'a', 'an', 'the', 'please']);

export function schedulingCommandStubParser(
  utterance: string,
  now: Date = new Date(),
): SchedulingCommandDraft | null {
  const rawTokens = tokenize(utterance);
  // Politeness fillers may precede the verb ("please book …") — skip them
  // before the verb check (corpus row: fillers+meridiem).
  let v = 0;
  while (v < rawTokens.length && FILLERS.has(rawTokens[v])) v++;
  const tokens = rawTokens.slice(v);
  if (tokens.length < 2 || tokens[0] !== 'book') return null;

  let period: 'Weeks' | 'Months' | undefined;
  let horizonCount: number | undefined;
  let horizonUnit: 'months' | 'weeks' | undefined;
  let weekday: string | undefined;
  let relDay: 0 | 1 | undefined; // today=0 / tomorrow=1
  let time: { hour: number; minute: number } | undefined;

  // ── Single pass over the tokens: structured parts (day/time/recurrence/
  //    duration) can sit anywhere; free-text spans accumulate into the
  //    CURRENT segment ("then" starts a new one; "with" flips the current
  //    segment's collector to doctor words).
  const segments: ParsedSegment[] = [{ span: [], doctorWords: [] }];
  let seg = segments[0];
  let collector: 'span' | 'doctor' = 'span';

  for (let i = 1; i < tokens.length; i++) {
    const t = tokens[i];

    if (t === 'then') {
      seg = { span: [], doctorWords: [] };
      segments.push(seg);
      collector = 'span';
      continue;
    }
    if (t === 'with') {
      collector = 'doctor';
      continue;
    }
    if (t === 'monthly') {
      period = 'Months';
      continue;
    }
    if (t === 'weekly') {
      period = 'Weeks';
      continue;
    }
    if (t === 'every') {
      const next = tokens[i + 1];
      if (next === 'week' || next === 'weeks') period = 'Weeks';
      if (next === 'month' || next === 'months') period = 'Months';
      if (next === 'week' || next === 'weeks' || next === 'month' || next === 'months') i++;
      continue;
    }
    if (t === 'for' && /^\d+$/.test(tokens[i + 1] ?? '')) {
      const unit = tokens[i + 2];
      if (unit === 'month' || unit === 'months') {
        horizonCount = Number(tokens[i + 1]);
        horizonUnit = 'months';
        i += 2;
        continue;
      }
      if (unit === 'week' || unit === 'weeks') {
        horizonCount = Number(tokens[i + 1]);
        horizonUnit = 'weeks';
        i += 2;
        continue;
      }
    }
    if (FILLERS.has(t) || t === 'for') continue; // filler
    if (t === 'tomorrow') {
      relDay = 1;
      continue;
    }
    if (t === 'today') {
      relDay = 0;
      continue;
    }
    // "next <weekday>" — 'next' is a date modifier, not a name word (motion
    // session's catch: "with Dr Fatima next Tuesday" parsed the doctor as
    // "Fatima Next"). nextOccurrence() already resolves to the coming
    // occurrence, so the modifier itself is a no-op — it just must never
    // leak into a free-text span.
    if (t === 'next' && WEEKDAY_BY_TOKEN.has(tokens[i + 1] ?? '')) {
      continue;
    }
    const wd = WEEKDAY_BY_TOKEN.get(t);
    if (wd && !weekday) {
      weekday = wd;
      continue;
    }
    const parsedTime = parseTimeToken(t);
    if (parsedTime && !time) {
      time = parsedTime;
      continue;
    }
    // "[duration]" — "45 min" / "30 minutes" / "1 hour" — per segment.
    if (/^\d+$/.test(t)) {
      const unit = tokens[i + 1];
      if (unit === 'min' || unit === 'mins' || unit === 'minutes') {
        seg.durationMinutes = Number(t);
        i++;
        continue;
      }
      if (unit === 'hr' || unit === 'hrs' || unit === 'hour' || unit === 'hours') {
        seg.durationMinutes = Number(t) * 60;
        i++;
        continue;
      }
    }
    // Free-text word → current collector. A leading "dr"/"dr." on the
    // doctor collector is honorific noise, dropped.
    if (collector === 'doctor') {
      if (!(seg.doctorWords.length === 0 && (t === 'dr' || t === 'dr.'))) {
        seg.doctorWords.push(t);
      }
    } else {
      seg.span.push(t);
    }
  }

  const first = segments[0];
  if (first.span.length === 0) return null; // no patient words at all

  // Both shapes require a day (weekday or today/tomorrow) + time.
  if ((!weekday && relDay === undefined) || !time) return null;

  const title = (w: string) => w.charAt(0).toUpperCase() + w.slice(1);
  const patientQuery = first.span.slice(0, 2).map(title).join(' ');
  const profileWords = first.span.slice(2);
  const profileQuery = profileWords.length > 0 ? profileWords.join(' ') : undefined;
  const doctorQuery =
    first.doctorWords.length > 0 ? first.doctorWords.map(title).join(' ') : undefined;

  const firstStart =
    relDay !== undefined
      ? relativeDay(relDay, time.hour, time.minute, now)
      : nextOccurrence(weekday as string, time.hour, time.minute, now);

  let recurrence: SchedulingCommandRecurrence | undefined;
  if (period && weekday) {
    // Occurrence count: when the "for N <unit>" horizon matches the
    // period, N is the count; a weekly series over a months horizon
    // approximates 4 weeks/month; monthly over weeks rounds down to ≥1.
    // No horizon → 4 occurrences (demo default).
    let count = 4;
    if (horizonCount && horizonUnit) {
      if (period === 'Weeks') {
        count = horizonUnit === 'weeks' ? horizonCount : horizonCount * 4;
      } else {
        count =
          horizonUnit === 'months'
            ? horizonCount
            : Math.max(1, Math.floor(horizonCount / 4));
      }
    }
    recurrence = {
      period,
      count,
      weekday,
      time: `${pad(time.hour)}:${pad(time.minute)}`,
    };
  }

  // Later "then" segments → the back-to-back chain (empty ones dropped).
  const chain: SchedulingCommandChainSegment[] = segments.slice(1).flatMap((s) => {
    const p = s.span.length > 0 ? s.span.join(' ') : undefined;
    const d = s.doctorWords.length > 0 ? s.doctorWords.map(title).join(' ') : undefined;
    if (!p && !d && !s.durationMinutes) return [];
    return [
      {
        ...(p ? { profileQuery: p } : {}),
        ...(d ? { doctorQuery: d } : {}),
        ...(s.durationMinutes ? { durationMinutes: s.durationMinutes } : {}),
      },
    ];
  });

  return {
    raw: utterance.trim(),
    patientQuery,
    ...(profileQuery ? { profileQuery } : {}),
    ...(doctorQuery ? { doctorQuery } : {}),
    ...(recurrence ? { recurrence } : {}),
    start: toLocalNoZISO(firstStart),
    ...(first.durationMinutes ? { durationMinutes: first.durationMinutes } : {}),
    ...(chain.length > 0 ? { chain } : {}),
  };
}
